import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for handling scroll-based effects and animations
 * @param {Object} options - Configuration options
 * @param {number} options.maxScroll - Maximum scroll distance for full effect (default: 150)
 * @param {number} options.throttleMs - Throttle interval in milliseconds (default: 16)
 * @param {boolean} options.enableBlur - Enable blur effects (default: true)
 * @param {boolean} options.enableElevation - Enable elevation effects (default: true)
 * @param {boolean} options.enableOpacity - Enable opacity effects (default: true)
 * @returns {Object} Scroll data and utility functions
 */
export const useScrollEffects = ({
  maxScroll = 150,
  throttleMs = 16,
  enableBlur = true,
  enableElevation = true,
  enableOpacity = true
} = {}) => {
  const [scrollData, setScrollData] = useState({
    scrollY: 0,
    scrollProgress: 0,
    opacity: 0.95,
    blur: 0,
    elevation: 0,
    backgroundOpacity: 0.95,
    borderOpacity: 0,
    isScrolled: false
  });

  const [isScrolling, setIsScrolling] = useState(false);

  // Throttled scroll handler
  const handleScroll = useCallback(() => {
    const scrollTop = window.scrollY;
    const scrollProgress = Math.min(scrollTop / maxScroll, 1);
    
    const newScrollData = {
      scrollY: scrollTop,
      scrollProgress,
      isScrolled: scrollTop > 10,
      opacity: enableOpacity ? 0.95 + (scrollProgress * 0.05) : 0.95,
      blur: enableBlur ? scrollProgress * 20 : 0,
      elevation: enableElevation ? scrollProgress * 8 : 0,
      backgroundOpacity: 0.95 + (scrollProgress * 0.05),
      borderOpacity: scrollProgress
    };

    setScrollData(newScrollData);
  }, [maxScroll, enableBlur, enableElevation, enableOpacity]);

  // Throttled scroll handler with requestAnimationFrame
  const throttledHandleScroll = useCallback(() => {
    if (!isScrolling) {
      setIsScrolling(true);
      requestAnimationFrame(() => {
        handleScroll();
        setTimeout(() => setIsScrolling(false), throttleMs);
      });
    }
  }, [handleScroll, throttleMs, isScrolling]);

  useEffect(() => {
    // Initial call to set initial state
    handleScroll();

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [throttledHandleScroll, handleScroll]);

  // Utility function to get CSS properties for AppBar
  const getAppBarStyles = useCallback(() => ({
    backgroundColor: `rgba(248, 249, 250, ${scrollData.backgroundOpacity})`,
    backdropFilter: `blur(${scrollData.blur}px)`,
    WebkitBackdropFilter: `blur(${scrollData.blur}px)`,
    boxShadow: scrollData.elevation > 0 
      ? `0 ${scrollData.elevation}px ${scrollData.elevation * 2}px rgba(0, 0, 0, ${0.1 * scrollData.opacity})` 
      : 'none',
    borderBottom: `1px solid rgba(222, 226, 230, ${scrollData.borderOpacity})`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    zIndex: 1100,
  }), [scrollData]);

  // Utility function to get CSS properties for content elements
  const getContentStyles = useCallback((baseOpacity = 1) => ({
    opacity: baseOpacity * scrollData.opacity,
    transform: `translateY(${scrollData.scrollY * 0.1}px)`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  }), [scrollData]);

  // Utility function to check if element is in viewport
  const isInViewport = useCallback((element) => {
    if (!element) return false;
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  }, []);

  // Utility function to add reveal animation to elements
  const addRevealAnimation = useCallback((selector) => {
    const elements = document.querySelectorAll(selector);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    elements.forEach((el) => {
      el.classList.add('reveal-on-scroll');
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return {
    scrollData,
    isScrolling,
    getAppBarStyles,
    getContentStyles,
    isInViewport,
    addRevealAnimation,
    // Utility functions
    scrollToTop: () => window.scrollTo({ top: 0, behavior: 'smooth' }),
    scrollToElement: (element, offset = 0) => {
      if (element) {
        const elementTop = element.offsetTop - offset;
        window.scrollTo({ top: elementTop, behavior: 'smooth' });
      }
    }
  };
};

/**
 * Hook for parallax effects
 * @param {number} speed - Parallax speed multiplier (default: 0.5)
 * @returns {Object} Parallax data
 */
export const useParallax = (speed = 0.5) => {
  const [parallaxY, setParallaxY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      setParallaxY(scrolled * speed);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return { parallaxY };
};

/**
 * Hook for scroll-triggered animations
 * @param {string} triggerClass - CSS class to add when scrolled
 * @param {number} threshold - Scroll threshold to trigger animation
 * @returns {Object} Animation state
 */
export const useScrollTrigger = (triggerClass = 'scrolled', threshold = 50) => {
  const [isTriggered, setIsTriggered] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.pageYOffset > threshold;
      setIsTriggered(scrolled);
      
      if (scrolled) {
        document.body.classList.add(triggerClass);
      } else {
        document.body.classList.remove(triggerClass);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.body.classList.remove(triggerClass);
    };
  }, [triggerClass, threshold]);

  return { isTriggered };
}; 